<template>
  <v-card
    ref="paletteContainer"
    elevation="6"
    :style="paletteStyles"
    :class="'palette-container d-flex flex-column-reverse d-sm-block'"
    absolute
  >
    <v-toolbar ref="palettetoolbar" flat color="contrast-grey-low" :height="parseInt(toolbarHeight)">
      <v-tooltip location="left">
        <template #activator="{ props }">
          <v-btn
            icon="mdi-palette"
            v-bind="props"
            :color="minimized ? 'font' : 'primary'"
            density="compact"
            @click="minMax"
          >
          </v-btn>
        </template>
        <span>Show/Hide Palette</span>
      </v-tooltip>
    </v-toolbar>
    <statechart-stencil v-if="!minimized" />
  </v-card>
</template>

<script setup lang="ts">
import DragHandler from "./services/drag-handler";
import StatechartStencil from "./StatechartStencil.vue";
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useDisplay } from "vuetify";
import { toolbarHeight, CardPosition, foldDirection } from "./utils/resizeCard";

const { xs, height } = useDisplay();

const paletteContainer = ref();
const paletteContentHeight = "420px";
const paletteMaxHeight = parseInt(paletteContentHeight) + parseInt(toolbarHeight) + "px";

const palettetoolbar = ref();

const compProps = withDefaults(
  defineProps<{
    right?: string;
    transition?: string;
  }>(),
  { right: "20px", transition: "none" }
);

const position = reactive<CardPosition>({
  xLeft: "unset",
  xRight: compProps.right,
  yTop: "unset",
  yBottom: "80px",
});

watch(
  () => compProps.right,
  (value: string) => {
    position.xLeft = "unset";
    position.xRight = value;
    position.yTop = "unset";
    position.yBottom = "80px";
  }
);

const paletteStyles = computed<string>(() => {
  if (minimized.value) {
    return `
      height: ${toolbarHeight};
      width: 50px;
      resize: none;
      transition: ${compProps.transition};
      left: ${position.xLeft};
      right: ${position.xRight};
      top: ${position.yTop};
      bottom: ${position.yBottom};
      `;
  } else {
    return `
      height: ${paletteMaxHeight};
      width: 50px;
      resize: none;
      transition: ${compProps.transition};
      left: ${position.xLeft};
      right: ${position.xRight};
      top: ${position.yTop};
      bottom: ${position.yBottom};
      `;
  }
});

watch(height, (newVal: number) => {
  if (newVal < parseInt(paletteMaxHeight)) {
    position.xLeft = "unset";
    position.xRight = "20px";
    position.yTop = "65px";
    position.yBottom = "unset";
    if (!minimized.value) {
      minimize();
    }
  }
});

const minimized = ref(false);

const dragContent = ref<HTMLElement>();

onMounted(() => {
  minimized.value = xs.value;
  const dragTarget = palettetoolbar.value.$el as HTMLElement;
  dragContent.value = paletteContainer.value.$el as HTMLElement;
  new DragHandler(dragTarget, dragContent.value, position).enableDragging();
});

function minMax() {
  minimized.value ? maximize() : minimize();
}

function minimize() {
  if (foldDirection(dragContent, "min", height.value) === "upwards") {
    position.yTop = dragContent.value.getBoundingClientRect().top + "px";
  } else {
    position.yTop =
      Math.min(
        dragContent.value.getBoundingClientRect().bottom - parseInt(toolbarHeight),
        height.value - parseInt(toolbarHeight) - 20
      ) + "px";
  }
  minimized.value = true;
}

function maximize() {
  if (foldDirection(dragContent, "max", height.value) === "upwards") {
    position.yTop =
      Math.max(
        dragContent.value.getBoundingClientRect().top - parseInt(paletteContentHeight),
        parseInt(toolbarHeight) + 20
      ) + "px";
  } else {
    position.yTop =
      Math.min(dragContent.value.getBoundingClientRect().top, height.value - parseInt(paletteMaxHeight) - 20) + "px";
  }
  minimized.value = false;
}
</script>
<style scoped>
.stencil-container {
  position: absolute;
  top: v-bind(toolbarHeight);
  height: 422px;
  width: 50px;
}

/* Media queries */
/* TODO check media queries
@media (min-width: 600px) and (max-width: 959.98px) {
  .palette-container {
    top: 125px;
  }
}
@media (max-width: 599.98px) {
  .palette-container {
    top: unset;
    right: 30px;
    bottom: 0;
  }
  .palette-container header {
    z-index: 9;
  }
  .stencil-container {
    position: unset;
  }
}
@media (max-height: 564.98px) {
  .palette-container {
    top: unset;
    right: 30px;
    bottom: 0;
  }
  .palette-container header {
    z-index: 9;
  }
  .stencil-container {
    position: unset;
  }
}
*/
</style>
<style>
.joint-stencil {
  border: 0px !important;
}
</style>
